import { useState, useMemo } from "react";
import { useOpsEngagements, type OpsV2Error } from "../../../api/opsV2";
import { AppShell } from "../../layouts/AppShell";
import { ForbiddenPanel } from "../../components/ops/ForbiddenPanel";
import { NotAccessiblePanel } from "../../components/ops/NotAccessiblePanel";
import { ArrowLeft, FolderOpen } from "lucide-react";

const STATUS_LABELS: Record<string, string> = {
  pipeline: "Pipeline",
  accepted: "Accepté",
  active: "Actif",
  blocked: "Bloqué",
  completed: "Terminé",
  cancelled: "Annulé",
};

type EngagementListPageProps = {
  userName?: string;
  onBackToDashboard?: () => void;
  onOpenEngagement?: (id: string) => void;
};

export function EngagementListPage({
  userName = "Utilisateur",
  onBackToDashboard,
  onOpenEngagement,
}: EngagementListPageProps) {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const { data: engagements, loading, error, refetch } = useOpsEngagements({
    status: statusFilter || undefined,
    limit: 100,
  });

  const apiError = error as OpsV2Error | undefined;
  const is403 = apiError?.status === 403;
  const is404 = apiError?.status === 404;

  const modules = useMemo(
    () => [
      { label: "Tableau de bord", onClick: onBackToDashboard },
      { label: "Dossiers", onClick: () => {} },
    ],
    [onBackToDashboard]
  );

  if (is403) {
    return (
      <AppShell
        topbar={<div className="text-sm text-muted">Opérations</div>}
        userName={userName}
        modules={modules}
      >
        <ForbiddenPanel
          message={error?.message}
          onBack={onBackToDashboard ? () => onBackToDashboard() : undefined}
        />
      </AppShell>
    );
  }

  if (is404 || (error && !loading)) {
    return (
      <AppShell
        topbar={<div className="text-sm text-muted">Opérations</div>}
        userName={userName}
        modules={modules}
      >
        <NotAccessiblePanel
          message={error?.message}
          onBack={onBackToDashboard ? () => onBackToDashboard() : undefined}
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Dossiers</div>}
      userName={userName}
      modules={modules}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {onBackToDashboard && (
              <button
                type="button"
                onClick={onBackToDashboard}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text hover:bg-surface-muted"
              >
                <ArrowLeft className="h-4 w-4" />
                Retour
              </button>
            )}
            <h1 className="text-section text-text">Dossiers</h1>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground" htmlFor="status-filter">
              Statut
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text"
            >
              <option value="">Tous</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-border bg-surface p-8 text-center text-sm text-muted-foreground">
            Chargement…
          </div>
        ) : !engagements?.length ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-surface p-12 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-sm font-medium text-text">Aucun dossier accessible</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Changez d’organisation dans le sélecteur si vous avez accès à d’autres dossiers.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-4 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
            >
              Actualiser
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-surface">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-surface-muted/50">
                <tr>
                  <th className="px-4 py-3 font-medium text-text">Statut</th>
                  <th className="px-4 py-3 font-medium text-text">Titre</th>
                  <th className="px-4 py-3 font-medium text-text">Type</th>
                  <th className="px-4 py-3 font-medium text-text">Dernière MAJ</th>
                  <th className="px-4 py-3 font-medium text-text" />
                </tr>
              </thead>
              <tbody>
                {engagements.map((e) => (
                  <tr
                    key={e.id}
                    className="border-b border-border last:border-0 hover:bg-surface-muted/30"
                  >
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                        {STATUS_LABELS[e.status] ?? e.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-text">{e.title}</td>
                    <td className="px-4 py-3 text-muted-foreground">{e.engagement_type}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {e.updated_at ? new Date(e.updated_at).toLocaleDateString("fr-CA") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {onOpenEngagement && (
                        <button
                          type="button"
                          onClick={() => onOpenEngagement(e.id)}
                          className="text-sm font-medium text-accent hover:underline"
                        >
                          Ouvrir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
